(() => {
  const TARGET_OPTION_NAME = 'select option';
  const ALT_MARKER = '| variant:';

  // If true, images without "| variant:" will show for all variants.
  // Good for size chart, care image, common lifestyle image, etc.
  const SHOW_UNGROUPED_MEDIA = true;

  const normalize = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const cleanOptionLabelText = (element) => {
    if (!element) return '';

    const cloned = element.cloneNode(true);

    cloned.querySelectorAll('[data-selected-value], .visually-hidden').forEach((child) => {
      child.remove();
    });

    return cloned.textContent.replace(':', '').trim();
  };

  const getSelectedOptionValue = (productInfo) => {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return '';

    const targetOptionKey = normalize(TARGET_OPTION_NAME);

    const optionGroups = Array.from(
      variantSelects.querySelectorAll('.product-form__input')
    );

    for (const group of optionGroups) {
      const legend = group.querySelector('legend');
      const label = group.querySelector('label.form__label');

      const optionName = cleanOptionLabelText(legend || label);

      if (normalize(optionName) !== targetOptionKey) continue;

      const checkedRadio = group.querySelector('input[type="radio"]:checked');
      if (checkedRadio) return checkedRadio.value;

      const select = group.querySelector('select');
      if (select) return select.value;
    }

    // Extra fallback for your Dawn button mode:
    // radio name can be like "select option-2"
    const checkedFallback = variantSelects.querySelector(
      'input[type="radio"]:checked'
    );

    if (
      checkedFallback &&
      normalize(checkedFallback.name).startsWith(targetOptionKey)
    ) {
      return checkedFallback.value;
    }

    return '';
  };

  const getSelectedVariant = (productInfo) => {
    const script = productInfo.querySelector(
      'variant-selects script[data-selected-variant]'
    );

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  const extractVariantKeyFromImage = (img) => {
    if (!img) return '';

    const originalAlt = img.getAttribute('alt') || '';
    const lowerAlt = originalAlt.toLowerCase();
    const markerIndex = lowerAlt.lastIndexOf(ALT_MARKER);

    if (markerIndex === -1) return '';

    const cleanAlt = originalAlt.slice(0, markerIndex).trim();
    const variantValue = originalAlt
      .slice(markerIndex + ALT_MARKER.length)
      .trim();

    // Hide the technical marker from actual page alt text.
    if (cleanAlt) {
      img.setAttribute('alt', cleanAlt);
    }

    return normalize(variantValue);
  };

  const prepareItems = (items) => {
    items.forEach((item) => {
      if (item.dataset.variantGalleryPrepared === 'true') return;

      const img = item.querySelector('img');
      const key = extractVariantKeyFromImage(img);

      item.dataset.variantGalleryKey = key;
      item.dataset.variantGalleryPrepared = 'true';
    });
  };

  const shouldShowItem = (item, selectedKey) => {
    const itemKey = item.dataset.variantGalleryKey || '';

    return (
      (itemKey && itemKey === selectedKey) ||
      (!itemKey && SHOW_UNGROUPED_MEDIA)
    );
  };

  const getSectionIdFromGallery = (gallery) => {
    return String(gallery.id || '').replace('MediaGallery-', '');
  };

  const setActiveVisibleMedia = (gallery, visibleSlides, selectedVariant) => {
    if (!visibleSlides.length) return;

    const sectionId = getSectionIdFromGallery(gallery);
    const featuredMediaId = selectedVariant?.featured_media?.id;

    let targetSlide = null;

    if (featuredMediaId) {
      targetSlide = visibleSlides.find((slide) => {
        return slide.dataset.mediaId === `${sectionId}-${featuredMediaId}`;
      });
    }

    if (!targetSlide) {
      targetSlide = visibleSlides[0];
    }

    if (targetSlide && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(targetSlide.dataset.mediaId, true);
    } else if (targetSlide) {
      gallery.querySelectorAll('li[data-media-id]').forEach((slide) => {
        slide.classList.remove('is-active');
      });
      targetSlide.classList.add('is-active');
    }
  };

  const updateSliderCounter = (gallery, visibleCount) => {
    gallery.querySelectorAll('.slider-counter--current').forEach((counter) => {
      counter.textContent = visibleCount > 0 ? '1' : '0';
    });

    gallery.querySelectorAll('.slider-counter--total').forEach((counter) => {
      counter.textContent = visibleCount;
    });

    gallery.querySelectorAll('.slider-buttons').forEach((buttons) => {
      buttons.classList.toggle('hidden', visibleCount <= 1);
    });
  };

  const filterProductModal = (productInfo, selectedKey) => {
    const sectionId = productInfo.dataset.section;
    const modal = document.querySelector(`#ProductModal-${sectionId}`);

    if (!modal) return;

    const modalItems = Array.from(
      modal.querySelectorAll('.product-media-modal__content > *')
    );

    modalItems.forEach((item) => {
      if (item.dataset.variantGalleryPrepared !== 'true') {
        const img = item.matches('img') ? item : item.querySelector('img');
        const key = extractVariantKeyFromImage(img);

        item.dataset.variantGalleryKey = key;
        item.dataset.variantGalleryPrepared = 'true';
      }

      const show = shouldShowItem(item, selectedKey);

      item.hidden = !show;
      item.classList.toggle('variant-gallery-hidden', !show);
    });
  };

  const filterGallery = (productInfo) => {
    const selectedOptionValue = getSelectedOptionValue(productInfo);
    const selectedKey = normalize(selectedOptionValue);

    if (!selectedKey) return;

    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery) return;

    const mainSlides = Array.from(
      gallery.querySelectorAll('[id^="Slider-Gallery-"] li[data-media-id]')
    );

    const thumbnails = Array.from(
      gallery.querySelectorAll('[id^="Slider-Thumbnails-"] li[data-target]')
    );

    prepareItems(mainSlides);
    prepareItems(thumbnails);

    const visibleSlides = [];

    mainSlides.forEach((slide) => {
      const show = shouldShowItem(slide, selectedKey);

      slide.hidden = !show;
      slide.classList.toggle('variant-gallery-hidden', !show);

      if (show) visibleSlides.push(slide);
    });

    thumbnails.forEach((thumbnail) => {
      const show = shouldShowItem(thumbnail, selectedKey);

      thumbnail.hidden = !show;
      thumbnail.classList.toggle('variant-gallery-hidden', !show);
    });

    const selectedVariant = getSelectedVariant(productInfo);

    setActiveVisibleMedia(gallery, visibleSlides, selectedVariant);
    updateSliderCounter(gallery, visibleSlides.length);
    filterProductModal(productInfo, selectedKey);

    window.dispatchEvent(new Event('resize'));
  };

  const filterAllProductGalleries = () => {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      filterGallery(productInfo);
    });
  };

  const delayedFilter = () => {
    window.setTimeout(filterAllProductGalleries, 350);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', delayedFilter);
  } else {
    delayedFilter();
  }

  document.addEventListener('shopify:section:load', delayedFilter);

  document.addEventListener('change', (event) => {
    if (!event.target.closest('variant-selects')) return;
    delayedFilter();
  });

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    if (PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, delayedFilter);
    }

    if (PUB_SUB_EVENTS.optionValueSelectionChange) {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, delayedFilter);
    }
  }
})();